import { useEffect, useState } from "react";
import {
  APPOINTMENTS_DEFER_HEAVY_PROVIDERS_ENABLED,
  APPOINTMENTS_SERVER_PAGING_ENABLED,
  COMMAND_DASHBOARD_DEFER_HEAVY_PROVIDERS_ENABLED,
  COMMAND_DASHBOARD_SERVER_PAGING_ENABLED,
  CONTRACTOR_ONBOARDING_DEFER_HEAVY_PROVIDERS_ENABLED,
  CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED,
  CONTRACTORS_DEFER_HEAVY_PROVIDERS_ENABLED,
  CONTRACTORS_SERVER_PAGING_ENABLED,
  CUSTOMERS_DEFER_HEAVY_PROVIDERS_ENABLED,
  CUSTOMERS_SERVER_PAGING_ENABLED,
  EXECUTION_DEFER_HEAVY_PROVIDERS_ENABLED,
  EXECUTION_SERVER_PAGING_ENABLED,
  GROWTH_CHANNELS_DEFER_HEAVY_PROVIDERS_ENABLED,
  GROWTH_CHANNELS_SERVER_PAGING_ENABLED,
  INSTALLER_OFFERS_DEFER_HEAVY_PROVIDERS_ENABLED,
  INSTALLER_OFFERS_SERVER_PAGING_ENABLED,
  INTAKE_REVIEW_DEFER_HEAVY_PROVIDERS_ENABLED,
  INTAKE_REVIEW_SERVER_PAGING_ENABLED,
  JOB_DETAIL_DEFER_HEAVY_PROVIDERS_ENABLED,
  JOB_DETAIL_SERVER_QUERY_ENABLED,
  ORDERING_QUEUE_DEFER_HEAVY_PROVIDERS_ENABLED,
  ORDERING_QUEUE_SERVER_PAGING_ENABLED,
  PERMITTING_DEFER_HEAVY_PROVIDERS_ENABLED,
  PERMITTING_SERVER_PAGING_ENABLED,
  PIPELINE_DEFER_HEAVY_PROVIDERS_ENABLED,
  PIPELINE_SERVER_PAGING_ENABLED,
  PROJECTS_DEFER_HEAVY_PROVIDERS_ENABLED,
  PROJECTS_SERVER_PAGING_ENABLED,
  RESOLVE_SERVICE_DEFER_HEAVY_PROVIDERS_ENABLED,
  RESOLVE_SERVICE_SERVER_PAGING_ENABLED,
  RESOLVE_DEFER_HEAVY_PROVIDERS_ENABLED,
  RESOLVE_SERVER_PAGING_ENABLED,
  REPORTS_DEFER_HEAVY_PROVIDERS_ENABLED,
  REPORTS_SERVER_PAGING_ENABLED,
  SCHEDULING_DISPATCH_DEFER_HEAVY_PROVIDERS_ENABLED,
  SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED,
  WAREHOUSE_DEFER_HEAVY_PROVIDERS_ENABLED,
  WAREHOUSE_SERVER_PAGING_ENABLED,
} from "@/lib/dataRuntimeFlags";

const NAVIGATION_EVENT = "koredor:navigation";
let historyPatched = false;

type BrowserLocationSnapshot = {
  pathname: string;
  search: string;
};

function readBrowserLocation(): BrowserLocationSnapshot {
  if (typeof window === "undefined") return { pathname: "/", search: "" };
  return {
    pathname: window.location.pathname,
    search: window.location.search,
  };
}

function dispatchNavigationEvent() {
  if (typeof window === "undefined") return;
  queueMicrotask(() => window.dispatchEvent(new Event(NAVIGATION_EVENT)));
}

function scheduleNavigationEvent() {
  queueMicrotask(dispatchNavigationEvent);
}

function ensureHistoryPatched() {
  if (historyPatched || typeof window === "undefined") return;
  historyPatched = true;
  const patch = (method: "pushState" | "replaceState") => {
    const original = window.history[method];
    window.history[method] = function patchedHistoryMethod(...args) {
      const result = original.apply(this, args);
      scheduleNavigationEvent();
      return result;
    } as typeof original;
  };
  patch("pushState");
  patch("replaceState");
}

export function useBrowserLocationSnapshot(): BrowserLocationSnapshot {
  const [location, setLocation] = useState(readBrowserLocation);

  useEffect(() => {
    if (typeof window === "undefined") return;
    ensureHistoryPatched();
    const update = () => setLocation(readBrowserLocation());
    window.addEventListener("popstate", update);
    window.addEventListener(NAVIGATION_EVENT, update);
    return () => {
      window.removeEventListener("popstate", update);
      window.removeEventListener(NAVIGATION_EVENT, update);
    };
  }, []);

  return location;
}

export function useBrowserPathname(): string {
  return useBrowserLocationSnapshot().pathname;
}

export function isWarehouseServerPagedRoute(pathname: string): boolean {
  return pathname === "/warehouse";
}

export function isOrderingQueueServerPagedRoute(pathname: string): boolean {
  return pathname === "/ordering-queue";
}

export function isPipelineServerPagedRoute(pathname: string): boolean {
  return pathname === "/pipeline";
}

export function isGrowthChannelsServerPagedRoute(pathname: string): boolean {
  return pathname === "/growth-channels" || pathname === "/lead-sources";
}

export function isIntakeReviewServerPagedRoute(pathname: string): boolean {
  return pathname === "/intake-review";
}

export function isPermittingServerPagedRoute(pathname: string): boolean {
  return pathname === "/permitting";
}

export function isInstallerOffersServerPagedRoute(pathname: string): boolean {
  return pathname === "/installer-offers";
}

export function isSchedulingDispatchServerPagedRoute(pathname: string, search = ""): boolean {
  if (pathname !== "/scheduling-dispatch") return false;
  const params = new URLSearchParams(search);
  const lens = params.get("lens");
  return !lens || lens === "dispatch";
}

export function isExecutionServerPagedRoute(pathname: string, search = ""): boolean {
  if (pathname !== "/execution") return false;
  const params = new URLSearchParams(search);
  const tab = params.get("tab");
  return !tab || tab === "execute";
}

export function isResolveServiceServerPagedRoute(pathname: string, search = ""): boolean {
  if (pathname === "/service-tickets") return true;
  if (pathname !== "/resolve") return false;
  const params = new URLSearchParams(search);
  return params.get("focus") === "service" || params.get("tab") === "service";
}

export function isResolveServerPagedRoute(pathname: string, search = ""): boolean {
  if (pathname === "/service-tickets") return true;
  if (pathname !== "/resolve") return false;
  const params = new URLSearchParams(search);
  const focus = params.get("focus") ?? params.get("tab") ?? "callbacks";
  return ["callbacks", "holdbacks", "issues", "service", "friction"].includes(focus);
}

export function isContractorsServerPagedRoute(pathname: string): boolean {
  return pathname === "/contractors";
}

export function isContractorOnboardingServerPagedRoute(pathname: string): boolean {
  return pathname === "/contractor-onboarding";
}

export function isCustomersServerPagedRoute(pathname: string): boolean {
  return pathname === "/customers";
}

function prefersCockpitSkin(): boolean {
  if (typeof window === "undefined") return true;
  try {
    return window.localStorage?.getItem("uiSkin") !== "classic";
  } catch {
    return true;
  }
}

export function isCommandDashboardServerPagedRoute(pathname: string): boolean {
  return pathname === "/command-dashboard" && prefersCockpitSkin();
}

export function isReportsServerPagedRoute(pathname: string): boolean {
  return pathname === "/reports";
}

export function isJobDetailServerPagedRoute(pathname: string): boolean {
  const parts = pathname.split("/").filter(Boolean);
  return parts.length === 2 && parts[0] === "jobs" && Boolean(parts[1]) && prefersCockpitSkin();
}

function shouldDeferHeavyProvidersForAppointments(pathname: string): boolean {
  return (
    APPOINTMENTS_SERVER_PAGING_ENABLED &&
    APPOINTMENTS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    pathname === "/appointments"
  );
}

function shouldDeferHeavyProvidersForProjects(pathname: string): boolean {
  return (
    PROJECTS_SERVER_PAGING_ENABLED &&
    PROJECTS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    pathname === "/projects"
  );
}

function shouldDeferHeavyProvidersForWarehouse(pathname: string): boolean {
  return (
    WAREHOUSE_SERVER_PAGING_ENABLED &&
    WAREHOUSE_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isWarehouseServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForOrderingQueue(pathname: string): boolean {
  return (
    ORDERING_QUEUE_SERVER_PAGING_ENABLED &&
    ORDERING_QUEUE_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isOrderingQueueServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForPipeline(pathname: string): boolean {
  return (
    PIPELINE_SERVER_PAGING_ENABLED &&
    PIPELINE_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isPipelineServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForGrowthChannels(pathname: string): boolean {
  return (
    GROWTH_CHANNELS_SERVER_PAGING_ENABLED &&
    GROWTH_CHANNELS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isGrowthChannelsServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForIntakeReview(pathname: string): boolean {
  return (
    INTAKE_REVIEW_SERVER_PAGING_ENABLED &&
    INTAKE_REVIEW_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isIntakeReviewServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForPermitting(pathname: string): boolean {
  return (
    PERMITTING_SERVER_PAGING_ENABLED &&
    PERMITTING_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isPermittingServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForInstallerOffers(pathname: string): boolean {
  return (
    INSTALLER_OFFERS_SERVER_PAGING_ENABLED &&
    INSTALLER_OFFERS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isInstallerOffersServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForSchedulingDispatch(pathname: string, search: string): boolean {
  return (
    SCHEDULING_DISPATCH_SERVER_PAGING_ENABLED &&
    SCHEDULING_DISPATCH_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isSchedulingDispatchServerPagedRoute(pathname, search)
  );
}

function shouldDeferHeavyProvidersForExecution(pathname: string, search: string): boolean {
  return (
    EXECUTION_SERVER_PAGING_ENABLED &&
    EXECUTION_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isExecutionServerPagedRoute(pathname, search)
  );
}

function shouldDeferHeavyProvidersForResolveService(pathname: string, search: string): boolean {
  return (
    RESOLVE_SERVICE_SERVER_PAGING_ENABLED &&
    RESOLVE_SERVICE_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isResolveServiceServerPagedRoute(pathname, search)
  );
}

function shouldDeferHeavyProvidersForResolve(pathname: string, search: string): boolean {
  return (
    RESOLVE_SERVER_PAGING_ENABLED &&
    RESOLVE_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isResolveServerPagedRoute(pathname, search)
  );
}

function shouldDeferHeavyProvidersForContractors(pathname: string): boolean {
  return (
    CONTRACTORS_SERVER_PAGING_ENABLED &&
    CONTRACTORS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isContractorsServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForContractorOnboarding(pathname: string): boolean {
  return (
    CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED &&
    CONTRACTOR_ONBOARDING_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isContractorOnboardingServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForCustomers(pathname: string): boolean {
  return (
    CUSTOMERS_SERVER_PAGING_ENABLED &&
    CUSTOMERS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isCustomersServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForCommandDashboard(pathname: string): boolean {
  return (
    COMMAND_DASHBOARD_SERVER_PAGING_ENABLED &&
    COMMAND_DASHBOARD_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isCommandDashboardServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForReports(pathname: string): boolean {
  return (
    REPORTS_SERVER_PAGING_ENABLED &&
    REPORTS_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isReportsServerPagedRoute(pathname)
  );
}

function shouldDeferHeavyProvidersForJobDetail(pathname: string): boolean {
  return (
    JOB_DETAIL_SERVER_QUERY_ENABLED &&
    JOB_DETAIL_DEFER_HEAVY_PROVIDERS_ENABLED &&
    isJobDetailServerPagedRoute(pathname)
  );
}

export function useDeferHeavyProvidersForAppointments(): boolean {
  return shouldDeferHeavyProvidersForAppointments(useBrowserPathname());
}

export function useDeferHeavyProvidersForProjects(): boolean {
  return shouldDeferHeavyProvidersForProjects(useBrowserPathname());
}

export function useDeferHeavyProvidersForWarehouse(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForWarehouse(location.pathname);
}

export function useDeferHeavyProvidersForOrderingQueue(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForOrderingQueue(location.pathname);
}

export function useDeferHeavyProvidersForPipeline(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForPipeline(location.pathname);
}

export function useDeferHeavyProvidersForGrowthChannels(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForGrowthChannels(location.pathname);
}

export function useDeferHeavyProvidersForIntakeReview(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForIntakeReview(location.pathname);
}

export function useDeferHeavyProvidersForPermitting(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForPermitting(location.pathname);
}

export function useDeferHeavyProvidersForInstallerOffers(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForInstallerOffers(location.pathname);
}

export function useDeferHeavyProvidersForSchedulingDispatch(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForSchedulingDispatch(location.pathname, location.search);
}

export function useDeferHeavyProvidersForExecution(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForExecution(location.pathname, location.search);
}

export function useDeferHeavyProvidersForResolveService(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForResolveService(location.pathname, location.search);
}

export function useDeferHeavyProvidersForResolve(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForResolve(location.pathname, location.search);
}

export function useDeferHeavyProvidersForContractors(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForContractors(location.pathname);
}

export function useDeferHeavyProvidersForContractorOnboarding(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForContractorOnboarding(location.pathname);
}

export function useDeferHeavyProvidersForCustomers(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForCustomers(location.pathname);
}

export function useDeferHeavyProvidersForCommandDashboard(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForCommandDashboard(location.pathname);
}

export function useDeferHeavyProvidersForReports(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForReports(location.pathname);
}

export function useDeferHeavyProvidersForJobDetail(): boolean {
  const location = useBrowserLocationSnapshot();
  return shouldDeferHeavyProvidersForJobDetail(location.pathname);
}

export function useDeferHeavyProvidersForCurrentRoute(): boolean {
  const location = useBrowserLocationSnapshot();
  return (
    shouldDeferHeavyProvidersForAppointments(location.pathname) ||
    shouldDeferHeavyProvidersForProjects(location.pathname) ||
    shouldDeferHeavyProvidersForWarehouse(location.pathname) ||
    shouldDeferHeavyProvidersForOrderingQueue(location.pathname) ||
    shouldDeferHeavyProvidersForPipeline(location.pathname) ||
    shouldDeferHeavyProvidersForGrowthChannels(location.pathname) ||
    shouldDeferHeavyProvidersForIntakeReview(location.pathname) ||
    shouldDeferHeavyProvidersForPermitting(location.pathname) ||
    shouldDeferHeavyProvidersForInstallerOffers(location.pathname) ||
    shouldDeferHeavyProvidersForSchedulingDispatch(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForExecution(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForResolve(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForResolveService(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForContractors(location.pathname) ||
    shouldDeferHeavyProvidersForContractorOnboarding(location.pathname) ||
    shouldDeferHeavyProvidersForCustomers(location.pathname) ||
    shouldDeferHeavyProvidersForCommandDashboard(location.pathname) ||
    shouldDeferHeavyProvidersForReports(location.pathname) ||
    shouldDeferHeavyProvidersForJobDetail(location.pathname)
  );
}

export function useDeferOnboardingProviderForCurrentRoute(): boolean {
  const location = useBrowserLocationSnapshot();

  if (
    (CONTRACTORS_SERVER_PAGING_ENABLED && isContractorsServerPagedRoute(location.pathname)) ||
    (CONTRACTOR_ONBOARDING_SERVER_PAGING_ENABLED && isContractorOnboardingServerPagedRoute(location.pathname))
  ) {
    return false;
  }

  return (
    shouldDeferHeavyProvidersForAppointments(location.pathname) ||
    shouldDeferHeavyProvidersForProjects(location.pathname) ||
    shouldDeferHeavyProvidersForWarehouse(location.pathname) ||
    shouldDeferHeavyProvidersForOrderingQueue(location.pathname) ||
    shouldDeferHeavyProvidersForPipeline(location.pathname) ||
    shouldDeferHeavyProvidersForGrowthChannels(location.pathname) ||
    shouldDeferHeavyProvidersForIntakeReview(location.pathname) ||
    shouldDeferHeavyProvidersForPermitting(location.pathname) ||
    shouldDeferHeavyProvidersForInstallerOffers(location.pathname) ||
    shouldDeferHeavyProvidersForSchedulingDispatch(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForExecution(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForResolve(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForResolveService(location.pathname, location.search) ||
    shouldDeferHeavyProvidersForCommandDashboard(location.pathname) ||
    shouldDeferHeavyProvidersForReports(location.pathname) ||
    shouldDeferHeavyProvidersForJobDetail(location.pathname)
  );
}
